import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import FileUpload from './FileUpload';
import RedesignSlideView from './RedesignSlideView';
import RedesignPDFExport from '../services/RedesignPDFExport';
import { withBackend } from '../utils/api';

export default function RedesignWorkspace({ project, redesignData, onBack, onRedesignComplete }) {
  const [currentView, setCurrentView] = useState('upload'); // 'upload' | 'generating' | 'results'
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [generatingPreviews, setGeneratingPreviews] = useState(false);
  const [loadingStage, setLoadingStage] = useState('parsing'); // 'parsing' | 'analyzing' | 'generating'
  const [previewProgress, setPreviewProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState(null);
  const [localRedesignData, setLocalRedesignData] = useState(redesignData);

  useEffect(() => {
    if (redesignData) {
      setLocalRedesignData(redesignData);
      setCurrentView('results');
    }
  }, [redesignData]);

  const handleFileUpload = async (file, audienceInfo) => {
    try {
      setError(null);
      setCurrentView('generating');
      setGeneratingPreviews(true);
      setLoadingStage('parsing');

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('audience_info', JSON.stringify(audienceInfo));
      formData.append('model', 'pro'); // Always use Pro for redesign

      console.log('🎨 Starting PowerPoint Redesign analysis...');

      // Simulate stage progression
      setTimeout(() => setLoadingStage('analyzing'), 2000);
      setTimeout(() => setLoadingStage('generating'), 5000);

      // Call the backend endpoint
      const response = await fetch(withBackend('/analyze-redesign'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server response:', errorText);

        // Try to parse as JSON, fallback to text error
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Failed to generate redesign');
        } catch (e) {
          throw new Error(`Server error: ${response.status} - ${errorText.substring(0, 200)}`);
        }
      }

      const data = await response.json();

      console.log('✅ PowerPoint Redesign completed:', data);

      setLocalRedesignData(data);
      setGeneratingPreviews(false);
      setCurrentView('results');

      // Notify parent component
      if (onRedesignComplete) {
        onRedesignComplete({
          redesignData: data,
        });
      }

    } catch (error) {
      console.error('❌ Redesign failed:', error);
      setError(error.message);
      setGeneratingPreviews(false);
      setLoadingStage('parsing');
      setCurrentView('upload');
    }
  };

  const handleDownloadPDF = () => {
    if (!localRedesignData) return;

    try {
      const fileName = `PowerPoint_Redesign_${new Date().toISOString().split('T')[0]}.pdf`;
      RedesignPDFExport.exportRedesignToPDF(localRedesignData, fileName);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  const handleNextSlide = () => {
    if (localRedesignData && currentSlideIndex < localRedesignData.slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePreviousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={onBack}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Projects</span>
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <h1 className="text-2xl font-bold text-slate-900">
                PowerPoint Redesign
              </h1>
            </div>

            {currentView === 'results' && localRedesignData && (
              <Button
                onClick={handleDownloadPDF}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Upload View */}
        {currentView === 'upload' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Upload Your Presentation
              </h2>
              <p className="text-slate-600">
                Upload your PowerPoint file to generate AI-powered redesign suggestions with
                step-by-step instructions for all slides.
              </p>
            </div>
            <FileUpload
              onComplete={handleFileUpload}
              mode="redesign"
            />
          </div>
        )}

        {/* Generating View */}
        {currentView === 'generating' && (
          <div className="bg-white rounded-lg shadow-lg p-12">
            <div className="flex flex-col items-center justify-center space-y-6">
              <Loader2 className="w-16 h-16 text-purple-600 animate-spin" />
              <div className="text-center max-w-2xl">
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                  {loadingStage === 'parsing' && 'Parsing Your Presentation'}
                  {loadingStage === 'analyzing' && 'Analyzing Design & Content'}
                  {loadingStage === 'generating' && 'Generating AI Redesigns'}
                </h2>
                <p className="text-slate-600 mb-6">
                  {loadingStage === 'parsing' && 'Reading slides and extracting content...'}
                  {loadingStage === 'analyzing' && 'Running comprehensive Design Compass analysis...'}
                  {loadingStage === 'generating' && 'Creating AI-powered redesigns for all slides with Gemini 3 Pro...'}
                </p>

                {/* Stage Progress */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                    loadingStage === 'parsing' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${loadingStage === 'parsing' ? 'bg-purple-600 animate-pulse' : 'bg-slate-400'}`} />
                    <span className="text-sm font-medium">Parse</span>
                  </div>
                  <div className="w-8 h-px bg-slate-300" />
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                    loadingStage === 'analyzing' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${loadingStage === 'analyzing' ? 'bg-purple-600 animate-pulse' : 'bg-slate-400'}`} />
                    <span className="text-sm font-medium">Analyze</span>
                  </div>
                  <div className="w-8 h-px bg-slate-300" />
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                    loadingStage === 'generating' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${loadingStage === 'generating' ? 'bg-purple-600 animate-pulse' : 'bg-slate-400'}`} />
                    <span className="text-sm font-medium">Generate</span>
                  </div>
                </div>

                {previewProgress.total > 0 && (
                  <div className="mt-6">
                    <div className="text-sm font-medium text-purple-600 mb-2">
                      Generating preview {previewProgress.completed} of {previewProgress.total}
                    </div>
                    <div className="w-full max-w-md mx-auto bg-slate-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-purple-600 h-full transition-all duration-300"
                        style={{
                          width: `${(previewProgress.completed / previewProgress.total) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-500 mt-6">
                  This process may take 5-15 minutes depending on the number of slides
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Results View */}
        {currentView === 'results' && localRedesignData && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Redesign Complete
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    {localRedesignData.metadata.successful_previews} of {localRedesignData.metadata.slide_count} slides redesigned
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">
                    {currentSlideIndex + 1} / {localRedesignData.slides.length}
                  </div>
                  <div className="text-sm text-slate-500">Current Slide</div>
                </div>
              </div>
            </div>

            {/* Slide View */}
            {localRedesignData.slides[currentSlideIndex] && (
              <RedesignSlideView
                slide={localRedesignData.slides[currentSlideIndex]}
                slideNumber={currentSlideIndex + 1}
                totalSlides={localRedesignData.slides.length}
                onPrevious={handlePreviousSlide}
                onNext={handleNextSlide}
                analysis={localRedesignData.analysis}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
